import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect, useMemo, useRef } from 'react'
import { fetchOrderCancellationConfigs } from 'actions/OrderActions'

function useOrderCancellationConfigs(id?: string, orderId?: string): [
  cancellationConfig: App.OrderItemHotelCancellationConfig,
  cancellationConfigLoading: boolean,
  cancellationConfigError: any,
] {
  const dispatch = useAppDispatch()

  const cancellationConfigs = useAppSelector(state =>
    orderId ? state.orders.cancellationConfigs[orderId] : undefined,
  )
  const fetching = useAppSelector(state =>
    orderId ? state.orders.cancellationConfigsFetching[orderId] : false,
  )
  const error = useAppSelector(state =>
    orderId ? state.orders.cancellationConfigsErrors[orderId] : undefined,
  )

  const forceRefetchAttempted = useRef(false)

  useEffect(() => {
    if (!orderId) return

    if (!cancellationConfigs) {
      forceRefetchAttempted.current = false
      dispatch(fetchOrderCancellationConfigs(orderId))
      return
    }
    if (id && !cancellationConfigs[id] && !fetching && !error && !forceRefetchAttempted.current) {
      forceRefetchAttempted.current = true
      dispatch(fetchOrderCancellationConfigs(orderId, { force: true }))
    }
  }, [orderId, id, cancellationConfigs, fetching, error, dispatch])

  const config = cancellationConfigs?.[id ?? '']

  const orderItemCancellationConfig: App.OrderItemHotelCancellationConfig = useMemo(() => ({
    policyType: config?.policy_type || 'non-refundable',
    policyDeadline: config?.policy_deadline || '',
    canUseBookingProtection: config?.can_use_booking_protection || false,
    canUseInsuranceBookingProtection: config?.can_use_insurance_booking_protection ?? false,
    canUseCancellationProtection: config?.can_use_cancellation_protection || false,
    canAutoRefund: config?.can_auto_refund || false,
    canRequestRefund: config?.can_request_refund || false,
    canRefundToOrigin: config?.can_refund_to_origin || false,
    canRefundToCredit: config?.can_refund_to_credit || false,
    changeOfMindEndDate: config?.change_of_mind_end_date ?? undefined,
    canRefund: config?.can_refund || false,
    blockAutoRefundReasons: config?.block_auto_refund_reasons || [],
    canChangeDates: config?.can_change_dates ?? false,
    canExtendDates: config?.can_extend_dates ?? false,
    canRequestDateChange: config?.can_request_date_change ?? false,
    canUpgradePackage: config?.can_upgrade_package || false,
    luxProtectEligible: config?.lux_protect_eligible,
    jetstarVoucherEligible: config?.jetstar_voucher_eligible,
  }), [config])

  return [orderItemCancellationConfig, fetching || !config, error]
}

export default useOrderCancellationConfigs
